import { 
    PersonOutline as PersonOutlineIcon,
    Settings as SettingsIcon,
    DarkMode as DarkModeIcon,
    LightMode as LightModeIcon,
    Logout as LogoutIcon,
    ExpandMore as ExpandMoreIcon,
    AdminPanelSettings as AdminPanelSettingsIcon
} from '@mui/icons-material';
import {
    AppBar,
    Box,
    Button,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    useTheme,
    alpha
} from '@mui/material';
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAppTheme } from '../../context/ThemeContext';
import { useUserService } from '../../services/user.service';
import { useAdmin } from '../../context/AdminContext';

import Logo from '../sidebar/Logo';
import StatusIndicator from '../sidebar/StatusIndicator';

const FlowHeader: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { isDarkMode, toggleTheme } = useAppTheme();
    const navigate = useNavigate();
    const { logout } = useAuth();
    const userService = useUserService();
    const { isAdmin } = useAdmin();
    
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);
    const accountMenuRef = useRef<HTMLDivElement>(null);
    const accountButtonRef = useRef<HTMLButtonElement>(null);

    const handleAccountToggle = () => {
        setAccountMenuOpen(!accountMenuOpen);
    };

    const handleSettings = () => {
        setAccountMenuOpen(false);
        navigate('/settings');
    };

    const handleAdmin = () => {
        setAccountMenuOpen(false);
        navigate('/admin');
    };

    const handleThemeToggle = () => {
        setAccountMenuOpen(false);
        toggleTheme();
    };

    const handleUserLogout = async () => {
        setAccountMenuOpen(false);
        try {
            userService.log('Logout');
            await logout();
            // logout() will redirect to CT logout page
        } catch (error) {
            console.error('Failed to log out:', error);
        }
    };

    return (
        <>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    bgcolor: (theme) => theme.palette.mode === 'dark' 
                        ? alpha(theme.palette.background.paper, 0.95)
                        : 'rgb(249, 247, 245)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    zIndex: (theme) => theme.zIndex.appBar,
                }}
            >
                <Toolbar
                    sx={{
                        justifyContent: 'space-between',
                        minHeight: 56,
                        pl: { xs: '20px', sm: '80px' }, // 80px left padding for desktop
                        pr: { xs: '20px', sm: '80px' }, // 80px right padding for desktop
                    }}
                >
                    {/* Left side - Logo only */}
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                opacity: 0.9,
                                transform: 'translateY(-1px)',
                                filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15))',
                            }
                        }}
                        onClick={() => navigate('/integrations')}
                    >
                        <Box
                            sx={{
                                '& img': {
                                    width: { xs: 140, md: 180 },
                                    height: { xs: 42, md: 54 },
                                }
                            }}
                        >
                            <Logo />
                        </Box>
                    </Box>

                    {/* Center - Status Indicator */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        flex: 1,
                        px: 2
                    }}>
                        <StatusIndicator />
                    </Box>

                    {/* Right side - Account button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>

                        {/* Account menu */}
                        <Box ref={accountMenuRef} sx={{ position: 'relative' }}>
                            <Button
                                ref={accountButtonRef}
                                onClick={handleAccountToggle}
                                startIcon={<PersonOutlineIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                                endIcon={
                                    <ExpandMoreIcon sx={{ 
                                        fontSize: { xs: '1rem', sm: '1.25rem' },
                                        display: { xs: 'none', md: 'block' }
                                    }} />
                                }
                                sx={{
                                    color: theme.palette.text.primary,
                                    textTransform: 'none',
                                    fontSize: { xs: '0.8rem', sm: '1rem' },
                                    fontWeight: 500,
                                    px: { xs: 1, sm: 2 },
                                    py: 1,
                                    borderRadius: 2,
                                    minWidth: 'auto',
                                    bgcolor: accountMenuOpen ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    },
                                    '& .MuiButton-endIcon': {
                                        transform: accountMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s ease-in-out',
                                    }
                                }}
                            >
                                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                    {t('sidebar.menu.account') || 'Account'}
                                </Box>
                            </Button>

                            {/* Account dropdown menu */}
                            <Menu
                                anchorEl={accountButtonRef.current}
                                open={accountMenuOpen}
                                onClose={() => setAccountMenuOpen(false)}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                PaperProps={{
                                    elevation: 8,
                                    sx: {
                                        mt: 1.5,
                                        minWidth: 180,
                                        bgcolor: (theme) => theme.palette.background.paper,
                                        border: '1px solid',
                                        borderColor: (theme) => alpha(theme.palette.divider, 0.4),
                                        '& .MuiMenuItem-root': {
                                            px: 2,
                                            py: 1,
                                            '&:hover': {
                                                bgcolor: theme.palette.primary.main,
                                                color: '#ffffff',
                                                '& .MuiListItemIcon-root': {
                                                    color: '#ffffff',
                                                },
                                            },
                                        },
                                    },
                                }}
                            >
                                <MenuItem onClick={handleSettings}>
                                    <ListItemIcon sx={{ minWidth: 34 }}>
                                        <SettingsIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={t('sidebar.menu.settings')}
                                        primaryTypographyProps={{
                                            sx: { fontSize: '0.875rem', fontWeight: 500 }
                                        }}
                                    />
                                </MenuItem>

                                {/* Admin menu item - only visible to admins */}
                                {isAdmin && (
                                    <MenuItem onClick={handleAdmin}>
                                        <ListItemIcon sx={{ minWidth: 34 }}>
                                            <AdminPanelSettingsIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary={t('sidebar.menu.admin')}
                                            primaryTypographyProps={{
                                                sx: { fontSize: '0.875rem', fontWeight: 500 }
                                            }}
                                        />
                                    </MenuItem>
                                )}

                                <MenuItem onClick={handleThemeToggle}>
                                    <ListItemIcon sx={{ minWidth: 34 }}>
                                        {isDarkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={isDarkMode ? t('sidebar.menu.lightMode') : t('sidebar.menu.darkMode')}
                                        primaryTypographyProps={{
                                            sx: { fontSize: '0.875rem', fontWeight: 500 }
                                        }}
                                    />
                                </MenuItem>

                                <MenuItem onClick={handleUserLogout}>
                                    <ListItemIcon sx={{ minWidth: 34 }}>
                                        <LogoutIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={t('sidebar.menu.logout')}
                                        primaryTypographyProps={{
                                            sx: { fontSize: '0.875rem', fontWeight: 500 }
                                        }}
                                    />
                                </MenuItem>
                            </Menu>
                        </Box>
                    </Box>
                </Toolbar>
            </AppBar>
        </>
    );
};

export default FlowHeader;